/**
 * Checkbox component module.
 * @module shared/components/Checkbox
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import styled from 'styled-components/macro';
import {Icon} from '../../shared';
import {fill} from './cssUtils';
import {_openModal} from '../reducers/modals';

const _InnerWrapper = styled.div`
  //noinspection CssInvalidPropertyValue
  height: -moz-fit-content;
  height: fit-content;
  margin: 0;
  
  display: inline-flex;
  align-items: center;
`;

const _Wrapper = styled.div`
  display: inline-flex;

  ${props => fill(props.width)}

  //noinspection CssInvalidPropertyValue
  height: -moz-fit-content;
  height: fit-content;
  margin: ${props => props.margin};
  opacity: ${props => props.disabled ? '0.5' : '1'};
  align-items: center;

  &:hover {
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  }

  ${props => (props.checked === true || props.checked === 1)
          && `
          & svg #Background {
            opacity: 0.1;
          }
          & svg #Intermediate {
            opacity: 0;
          }
          & svg #Checkmark {
            opacity: 1;
          }
          `
          || (props.checked === false || props.checked === 0)
          && `
          & svg #Background {
            opacity: 0;
          }
          & svg #Intermediate {
            opacity: 0;
          }
          & svg #Checkmark {
            opacity: 0;
          }
          `
          || `
          & svg #Background {
            opacity: 0.05;
          }
          & svg #Intermediate {
            opacity: 1;
          }
          & svg #Checkmark {
            opacity: 0;
          }
          `}
  &:hover svg #Background {
    ${props => (!props.disabled && 'opacity: 0.15') || ''};
  }
`;

const _Label = styled.div`
  display: inline-block;
  margin-left: 5px;
  padding: 0;
  border: none;
  background-color: transparent;
  color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)'};
  text-align: left;
  font-family: ${props => props.theme.bodyText.name};
  font-size: calc(${props => props.theme.bodyText.size} + 2px);
  word-wrap: break-word;
`;

/**
 * Checkbox component
 * @name Checkbox
 */
const Checkbox = props => {
  const {title, dataPoint, width, onChange, disabled, processing, margin = '5px', help, openModal} = props;
  const checked = dataPoint.value;
  const [internalProcessing, setInternalProcessing] = React.useState();
  const onClickHandler = () => {
    if (disabled) {return;}
    const prom = onChange(dataPoint.name, !checked ? 1 : 0);
    if (prom && prom.then) {
      setInternalProcessing(true);
      prom.finally(() => setInternalProcessing(false));
    }
  };

  const type = help?.type || 'modal';

  return <_Wrapper
    margin={margin}
    width={width}
    checked={checked}
    disabled={disabled}
  >
    <_InnerWrapper onClick={onClickHandler}>
      <Icon margin={'0'} width={'20px'} height={'20px'} disabled={disabled || internalProcessing || processing}
            svg={'checkbox'}/>
      {title && <_Label>{title}</_Label>}
    </_InnerWrapper>
    {help && (
      <Icon
        height="16px"
        width="16px"
        margin="0 0 0 5px"
        description={(help.type === 'tooltip') ? help.description : null}
        svg={'help'}
        action={['action', 'modal'].includes(type) ? (e => {
          e?.stopPropagation();

          if (type === 'action') {
            help.action();
          } else if (type === 'modal') {
            openModal({
              type: 'MessageModal',
              size: help.size || 'sm',
              body: help.content,
              title: help.title,
              button: help.button,
            });
          } else {
            console.warn(`Checkbox: invalid type "${type}"`);
          }
        }) : null}
      />
    )}
  </_Wrapper>;
};

Checkbox.propTypes = {
  /**
   * the label title
   */
  title: PropTypes.string,
  /**
   * the data point to be bound to the parent - value is:
   * true/false bool for checked/unchecked
   * any other value is indeterminate - clicking on indeterminate checkbox will convert checkbox value to true (checked)
   */
  dataPoint: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.any,
  }).isRequired,
  /**
   * the width of the component
   */
  width: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  /**
   * on change function
   */
  onChange: PropTypes.func.isRequired,
  /**
   * disabled boolean
   */
  disabled: PropTypes.bool,
  /**
   * indeterminate boolean (if checkbox is indeterminate, then it cannot be toggled)
   */
  indeterminate: PropTypes.bool,
  /**
   * Indicate that the icon is processing
   */
  processing: PropTypes.bool,
  /**
   * CSS Margin defaults to 5px
   */
  margin: PropTypes.string,
  /**
   * If passed in, then help icon displays per content, description, title, and type properties
   */
  help: PropTypes.shape({
    /**
     * Help type
     */
    type: PropTypes.oneOf(['action', 'modal', 'tooltip']).isRequired,
    /**
     * Help icon description
     */
    description: PropTypes.string,
    /**
     * If help type is modal, the modal size. Defaults to sm
     */
    size: PropTypes.oneOf(['sm', 'md', 'lg']),
    /**
     * If help type is modal, the modal title
     */
    title: PropTypes.string,
    /**
     * If help type is modal, the modal body
     */
    content: PropTypes.node,
    /**
     * If help type is action, the action to perform
     */
    action: PropTypes.func,
    /**
     * If button needs to be customized, then pass the text, action, and data for button
     */
    button: PropTypes.shape({
      text: PropTypes.string,
      action: PropTypes.func,
      data: PropTypes.object,
    }),
  }),
  /**
   *  Opens modal on page
   */
  openModal: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  openModal: content => dispatch(_openModal(content)),
});

export default connect(null, mapDispatchToProps)(Checkbox);
